import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, ArrowRight, Lock, KeyRound } from "lucide-react";

export default function TeamSetup() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showCode, setShowCode] = useState(false);

  // Already placed in a team (admin allocation) → straight to the dashboard.
  useEffect(() => {
    if (user && user.team_id) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/teams/join", { join_code: code.trim().toUpperCase() });
      await refreshMe();
      toast.success("Joined your team");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center p-6" data-testid="team-setup-page">
      <div className="w-full max-w-2xl">
        <span className="weave-mark" style={{ width: 72, height: 5 }} />
        <div className="label-eyebrow mt-4">You&apos;re in</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-2">Welcome to iTeams, {user?.name?.split(" ")[0] || "there"}.</h1>
        <p className="mt-3 text-[#525252] text-sm max-w-lg">
          Your account is ready. Teams are allocated by the programme team — there&apos;s nothing you need to enter.
        </p>

        <div className="mt-8 border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-5 flex items-start gap-4" data-testid="team-setup-pending">
          <div className="w-10 h-10 bg-[#0F1F2E] text-[#F4B847] flex items-center justify-center shrink-0"><Lock size={18} /></div>
          <div>
            <div className="label-eyebrow text-[#7A5B17]">Coming at Session 1</div>
            <div className="font-display text-xl font-black tracking-tighter text-[#7A5B17] mt-1">Your team will be announced at Session 1.</div>
            <p className="text-sm text-[#7A5B17] mt-1">Until then, explore the programme, read the Session 1 pre-work and complete the entry survey.</p>
          </div>
        </div>

        <Button onClick={() => navigate("/dashboard", { replace: true })}
          className="mt-6 rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d] h-12 px-8 uppercase text-xs font-semibold tracking-wider"
          data-testid="team-setup-continue-btn">
          Explore the platform <ArrowRight size={14} className="ml-2" />
        </Button>

        <div className="mt-10 border-t border-[#E5E7EB] pt-6">
          {!showCode ? (
            <button type="button" onClick={() => setShowCode(true)} className="text-xs text-[#525252] inline-flex items-center gap-1.5 hover:text-[#0F1F2E] hover:underline" data-testid="team-setup-show-code">
              <KeyRound size={12} /> Were you given a team join code by the programme team? Enter it here
            </button>
          ) : (
            <form onSubmit={join} className="space-y-3 max-w-sm" data-testid="team-setup-code-form">
              <Label className="label-eyebrow">Team join code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. TEAM-A"
                className="rounded-none h-11 font-mono tracking-widest uppercase" data-testid="team-code-input" />
              {error && <div className="border border-[#e93e36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="team-setup-error">{error}</div>}
              <Button type="submit" disabled={busy || !code.trim()} variant="outline" className="rounded-none border-[#0A0A0A] h-10" data-testid="team-join-btn">
                <Users size={14} className="mr-2" /> {busy ? "Joining…" : "Join team"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
